/**
 * Phase 261g — Dashboard "Get App" quick-action button.
 *
 * Smart, persistent CTA that lives next to Add Student / Mark Attendance /
 * Create Event / Add Expense in the dashboard action grid. Gives academy
 * owners + coaches a discoverable install affordance that doesn't depend on
 * Chrome's `beforeinstallprompt` heuristics firing on the right page.
 *
 * Design contract (per Phase 261 doctrine):
 *   • Uses the existing `usePwaInstall` provider — does NOT add duplicate
 *     `beforeinstallprompt` listeners that would fight the provider or
 *     bypass PostHog telemetry.
 *   • Hides when the app is already installed or running as standalone.
 *   • When the user dismissed the banner (Phase 261b cooldown), clicking
 *     this button calls `clearPwaDismissal()` so the next dashboard visit
 *     resurfaces the full banner alongside this CTA.
 *   • Telemetry: pwa_install_prompted / _accepted / _rejected fire with
 *     the caller-provided `source` prop (defaults to 'dashboard_quick_action').
 *     Use 'student_portal_quick_action' on the StudentPortal mount so the
 *     PostHog funnel can attribute installs per landing surface.
 *   • iOS Safari path opens a small instructions toast — Safari has no
 *     native install prompt.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Smartphone, RefreshCw, Share } from 'lucide-react';
import { toast } from 'sonner';
import { usePwaInstall } from '@/lib/pwaInstall';
import { useAuth } from '@/contexts/AuthContext';
import { clearPwaDismissal, getPwaDismissalState } from '@/components/PwaInstallBanner';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';

export default function DashboardGetAppButton({ source = 'dashboard_quick_action' } = {}) {
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  // Don't render when the app is already pinned — no point nagging.
  if (installed || isStandalone) return null;

  const persona = personaForRole(user?.role);

  const handleClick = async () => {
    // 1. iOS Safari — Apple disallows programmatic install prompts.
    if (isIos && !canInstall) {
      trackPwa('pwa_install_prompted', { persona, role: user?.role, source, variant: 'ios' });
      toast.message('Add Out-Play to your Home Screen', {
        description: 'Tap the Share icon in Safari, then choose "Add to Home Screen".',
        icon: <Share className="h-4 w-4 text-sky-500" />,
        duration: 6000,
      });
      return;
    }

    // 2. Chrome / Edge / Brave installable — fire the native prompt.
    if (canInstall) {
      setBusy(true);
      trackPwa('pwa_install_prompted', { persona, role: user?.role, source });
      try {
        const accepted = await promptInstall();
        trackPwa(accepted ? 'pwa_install_accepted' : 'pwa_install_rejected', {
          persona, role: user?.role, source,
        });
        if (accepted) {
          toast.success('Out-Play installed — launch it from your taskbar or home screen.');
        } else {
          toast.info('Install cancelled. The button stays available — try again whenever you want.');
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    // 3. Pending state — browser hasn't fired beforeinstallprompt yet, OR
    //    the user dismissed the banner. Help them: clear any cooldown so
    //    the banner re-surfaces on the next dashboard visit and tell them
    //    what to do right now.
    const { dismissed } = getPwaDismissalState();
    if (dismissed) {
      clearPwaDismissal();
      trackPwa('pwa_card_cooldown_cleared', { persona, role: user?.role, source });
      toast.success('Install banner is back', {
        description: 'Refresh this page and look for the green install banner at the top of the dashboard.',
      });
      return;
    }

    // No dismissal active — guide them to the URL-bar install icon. Still
    // track the click: knowing "user actively clicked Get App but Chrome
    // hadn't met install heuristics yet" is signal for the funnel.
    trackPwa('pwa_install_prompted', {
      persona, role: user?.role, source, variant: 'browser_not_ready',
    });
    toast.message('Install prompt not ready yet', {
      description:
        'Chrome / Edge needs a few seconds of activity before it offers PWA install. ' +
        'Look for the install icon (⊕) at the right edge of your address bar — click it to pin Out-Play.',
      duration: 8000,
    });
  };

  // Pick label + icon based on state. Honest: no "AI Detected", no fake confidence.
  const isIosVariant = isIos && !canInstall;
  const Icon = isIosVariant ? Smartphone : busy ? RefreshCw : Download;
  const label = isIosVariant ? 'Add to Home Screen' : busy ? 'Opening installer…' : 'Get App';

  return (
    <Button
      size="sm"
      onClick={handleClick}
      disabled={busy}
      className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-700"
      data-testid="quick-action-get-app"
      data-pwa-state={canInstall ? 'eligible' : isIosVariant ? 'ios' : 'pending'}
      title="Pin Out-Play to your taskbar or home screen for one-tap launch"
    >
      <Icon className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''} text-emerald-400`} />
      {label}
    </Button>
  );
}
